import { BaseError } from 'make-error';

// https://www.typescriptlang.org/docs/handbook/release-notes/typescript-3-7.html#assertion-functions

export class AssertionError extends BaseError {
    constructor(message?: string) {
        super(message ?? 'The assertion is not true.');
    }
}

/**
 * Ensures that the condition being checked must be true for the remainder of the containing scope.
 * @param condition - (Takes a truthy value)
 * @param msg - A human-readable description of the assertion error.
 * @throws {@link AssertionError} if condition is not true.
 */
export function assert(condition: unknown, msg?: string): asserts condition {
    if (!condition) {
        throw new AssertionError(msg);
    }
}

/**
 * Used to ensure exhaustive checks have been made.
 *
 * @throws {@link AssertionError} if called.
 * @example
 * ```ts
 * enum Fruit {
 *     APPLE,
 *     ORANGE,
 *     PEAR
 * }
 *
 * function getFruit(fruitType: Fruit): string {
 *     switch (fruitType) {
 *         case Fruit.APPLE: return "Apple";
 *         case Fruit.ORANGE: return "Orange";
 *         // If a new case is added at compile time you will get a compile error
 *         // If a new value appears at runtime you will get a runtime error
 *         default: assertNever(fruitType);
 *         //                   ~~~~~~~~~
 *         // error: Argument of type 'Fruit' is not assignable to parameter of type 'never'.
 *     }
 * }
 * ```
 */
export function assertNever(shouldBeNever: never, message?: string): never {
    throw new AssertionError(message ?? `Unexpected value: '${String(shouldBeNever)}'.`);
}
